import { use, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { useLingui } from '@lingui/react/macro'
import { Cancel01Icon } from '@hugeicons/core-free-icons'
import { createIcon } from '../shared/navigation-config'
import { Button } from '@/components/ui/button'
import { DialogEnterContext, type EnterHandover } from '@/components/ui/dialog-surface'
import { cn } from '@/lib/utils'

// Its own icon, not assistant-icons.tsx: this file is on every first load, the chat's icons are not.
const XIcon = /* @__PURE__ */ createIcon(Cancel01Icon)
/**
 * The assistant's boxes, eager: the slot's pendingComponent and the skeletons (assistant-skeleton.tsx) draw them on
 * every first load, so nothing here imports the chat, base-ui or a query. What places and runs them is
 * assistant-frame.tsx, which loads with the sections and the slot route: docs/notes/app/assistant-panel.md.
 */

/** The page's own surface: the composer's field wears `secondary`, and a card is set off by its own edge. */
export const ASSISTANT_SURFACE_CLASS = 'bg-background'

/** A card in the chat (an overview, an answer's card): set off by its border, and in dark by the lighter `card`. */
export const ASSISTANT_CARD_CLASS = 'bg-card border'

// MARK: Expanded

// Below md it is a full-screen sheet; from md up a wide column under the top bar (h-18).
const EXPANDED_CLASS =
  'fixed inset-0 z-50 flex flex-col text-sm outline-none transition-[translate,opacity] duration-100 ease-out data-closed:translate-x-10 data-closed:opacity-0 md:top-18 md:left-auto md:w-[min(calc(100vw-3rem),max(70vw,56rem),68.75rem)]'

/**
 * The expanded view's surface: non-modal, so the page beside it keeps its scroll, clicks and focus. It takes Escape
 * only while focus is inside, and hands focus back to whatever opened it when it closes holding it.
 */
export function AssistantSurface(props: {
  open: boolean
  onClose: () => void
  label: string
  busy?: boolean
  children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const enterTaken = useEnterHandover(props.open)
  useReturnFocus(props.open, ref)
  const closeRef = useRef(props.onClose)
  useEffect(() => {
    closeRef.current = props.onClose
  })

  // A DOM listener: the chat is portalled in from the shell, so its keys bubble through this element but not
  // through this component's React tree; and a portalled popup (a confirm, a menu) is outside this element.
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      event.preventDefault()
      closeRef.current()
    }
    element.addEventListener('keydown', onKeyDown)
    return () => element.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={props.label}
      aria-busy={props.busy || undefined}
      data-assistant-panel=""
      {...(props.open ? { 'data-open': '' } : { 'data-closed': '' })}
      className={cn(
        EXPANDED_CLASS,
        ASSISTANT_SURFACE_CLASS,
        !enterTaken && 'starting:translate-x-10 starting:opacity-0',
      )}
    >
      {props.children}
    </div>
  )
}

/**
 * The skeleton's side and the panel's side of one enter: the skeleton marks it played as it unmounts while open, and
 * the panel that replaces it starts at rest instead of sliding in a second time.
 */
function useEnterHandover(open: boolean): boolean {
  const handover = use(DialogEnterContext)
  const [taken] = useState(() => takeEnter(handover))
  const openRef = useRef(open)
  useEffect(() => {
    openRef.current = open
  })
  useEffect(
    () => () => {
      if (handover && openRef.current) handover.startTime = document.timeline.currentTime
    },
    [handover],
  )
  return taken
}

function takeEnter(handover: EnterHandover | null): boolean {
  if (handover?.startTime == null) return false
  handover.startTime = null
  return true
}

function useReturnFocus(open: boolean, ref: RefObject<HTMLDivElement | null>) {
  // Read while rendering, before the composer's autofocus moves it.
  const [opener] = useState(() => (typeof document === 'undefined' ? null : document.activeElement))
  useEffect(() => {
    if (open || !ref.current?.contains(document.activeElement)) return
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
  }, [open, opener, ref])
}

// MARK: Pane

/**
 * The reading column of what the pane shows under its full-width header: the open floating chat's width, so the field
 * in it is the folded bar's.
 */
export const ASSISTANT_PANE_COLUMN_CLASS = 'mx-auto flex w-full max-w-[47rem] flex-1 flex-col'

/**
 * Where the pane's content starts under the page's header: 2rem, the inset the report's sections stand at under their
 * bar. A new conversation's overview and a stored one's first message both start there.
 */
export const ASSISTANT_PANE_TOP_CLASS = 'pt-8'

/**
 * The pane's foot: the chat's two controls just over its field, stuck to the viewport's foot, the content showing
 * through the fade above them; only they take clicks. `above` stands over it at the column's foot, unstuck (a new
 * conversation's recent ones), so what lands there never moves the content at the top or the field.
 */
export function AssistantPaneFoot(props: { controls: ReactNode; above?: ReactNode; children?: ReactNode }) {
  return (
    <>
      <div className="mt-auto flex flex-col">{props.above}</div>
      <div className="pointer-events-none sticky bottom-0 z-10 flex flex-col">
        <div className="from-background/0 to-background bg-linear-to-b pt-4" />
        <div className="bg-background flex flex-col gap-1 pb-2">
          <div className="pointer-events-auto flex items-center justify-end gap-0.5 px-2">{props.controls}</div>
          {props.children && <div className="pointer-events-auto">{props.children}</div>}
        </div>
      </div>
    </>
  )
}

// MARK: Shared pieces

/**
 * The chat's controls, small icons at its top right corner instead of a header bar; the conversation scrolls under
 * them with room left at its top (`ASSISTANT_CONTROLS_CLEARANCE`).
 */
export function AssistantControls(props: { children?: ReactNode; onClose?: () => void }) {
  const { t } = useLingui()
  return (
    <div className="bg-background/90 absolute top-2 right-2 z-10 flex items-center gap-0.5 rounded-lg p-0.5 backdrop-blur-sm">
      {props.children}
      {props.onClose && (
        <Button variant="ghost" size="icon-sm" aria-label={t`Close`} onClick={props.onClose}>
          <XIcon />
        </Button>
      )}
    </div>
  )
}

export const ASSISTANT_CONTROLS_CLEARANCE = 'pt-12'

/** Under the controls: the conversation alone, or wide the history as a standing column beside it. */
export function AssistantWorkspace(props: { wide: boolean; history: ReactNode; children: ReactNode }) {
  const { t } = useLingui()
  return (
    <div className="flex min-h-0 flex-1">
      {props.wide && (
        <aside
          aria-label={t`Conversations`}
          className={cn('border-foreground/5 flex w-72 shrink-0 flex-col border-r', ASSISTANT_CONTROLS_CLEARANCE)}
        >
          {props.history}
        </aside>
      )}
      <div className="flex min-w-0 flex-1 flex-col">{props.children}</div>
    </div>
  )
}

/** The reading column of the conversation and its composer: the place's width, capped where it is wide. */
export const ASSISTANT_COLUMN_CLASS = 'mx-auto w-full max-w-3xl'

/**
 * The composer's box: the field with an even margin, so folded the floating chat is the field alone. That answers can
 * be wrong is said once, in the terms the user accepts.
 */
export function AssistantComposerFrame(props: { children: ReactNode; flush?: boolean }) {
  return (
    <div className="shrink-0">
      <div
        data-assistant-composer=""
        className={cn(ASSISTANT_COLUMN_CLASS, 'flex flex-col gap-2', !props.flush && 'p-2')}
      >
        {props.children}
      </div>
    </div>
  )
}

/**
 * The field's frame: at rest a hairline barely off the page, so it never boxes in the text it floats over; focused,
 * the 2px darker frame (border plus an inset ring, so the text never shifts).
 */
export const ASSISTANT_INPUT_CLASS =
  'max-h-40 min-h-10 resize-none border border-foreground/10 bg-background pr-11 shadow-xs dark:bg-background focus:border-ring focus:ring-1 focus:ring-ring focus:ring-inset'
